import clsx from "clsx";
import { useTranslation } from "react-i18next";

import { Button as ButtonV2 } from "../../../Button";
import { CrossIcon } from "../../../Icons";
import { Typo } from "../../../Typo";
import { WizardFooter } from "./WizardFooter";

type DiscardConfirmOverlayProps = {
  /** Keeps the wizard open with everything intact. */
  onStay: () => void;
  /** Closes the wizard and throws the inputs away. */
  onDiscard: () => void;
  isMobile?: boolean;
};

/**
 * Confirmation shown when the user tries to dismiss the create-program wizard
 * with unsaved input — X, Esc, or a backdrop click.
 *
 * Canvas `.wiz-discard-overlay` + `.wiz-discard`: an overlay *inside* the wizard
 * panel that washes it out, **not** a second modal stacked on the first. Relies
 * on `ResponsiveModal`'s panel being `relative` (`ResponsiveModal/index.tsx:92`)
 * for `absolute inset-0` to cover exactly the panel, and matches its `rounded-2xl`
 * in place of the canvas's `border-radius: inherit`. The intervening scroll
 * container sets no `position`, so it is not the containing block and does not
 * clip this.
 *
 * Copy and button labels follow the canvas (`wiz.discard.*`) copy registry's
 * current values, not its stale JSX fallbacks — the primary action reads
 * "Leave", not "Discard" or "Confirm".
 */
export const DiscardConfirmOverlay = ({
  onStay,
  onDiscard,
  isMobile,
}: DiscardConfirmOverlayProps) => {
  const { t } = useTranslation();
  const title = t("subIBReport_wizardDiscardTitle", "Leave without saving?");

  return (
    <div
      className={clsx(
        "absolute inset-0 z-[2] flex items-center justify-center",
        !isMobile && "rounded-2xl",
      )}
    >
      {/* Canvas `background: rgba(255, 255, 255, 0.92)`. Split into its own layer
          because `surface-primary` carries a literal alpha, so Tailwind can't add
          one via a slash modifier — and putting `opacity` on the container would
          fade the card with it. Radius matches the parent shell — square on the
          mobile full-screen takeover, rounded on the desktop modal card. */}
      <div
        className={clsx(
          "absolute inset-0 bg-surface-primary opacity-[0.92]",
          !isMobile && "rounded-2xl",
        )}
        aria-hidden="true"
      />
      <div
        role="alertdialog"
        // Named by the heading's text rather than `aria-labelledby` — `Typo` has a
        // closed prop set and would drop an `id`.
        aria-label={title}
        data-testid="wizard-discard-modal"
        className="relative flex max-w-[360px] flex-col rounded-2xl border border-stroke-primary bg-surface-primary p-8 text-left shadow-dropdown"
      >
        {/* Canvas `.wiz-discard__head`: right-aligned close, 8px below it. */}
        <div className="flex min-h-6 items-center justify-end pb-2">
          <button
            type="button"
            aria-label={t("common_close", "Close")}
            onClick={onStay}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-icon-secondary transition-colors hover:bg-overlay-opacity-state-hover"
          >
            <CrossIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <Typo element="h3" type="headingLG" textColor="text-text-primary">
          {title}
        </Typo>
        <Typo
          element="p"
          type="bodyMD"
          textColor="text-text-secondary"
          className="mt-6"
        >
          {t("subIBReport_wizardDiscardBody", "Your entries won't be saved")}
        </Typo>
        <div className="mt-6">
          <WizardFooter isMobile={isMobile}>
            <ButtonV2
              variant="tonal"
              size="md"
              onClick={onStay}
              data-testid="wizard-discard-stay"
            >
              {t("subIBReport_wizardDiscardStay", "Stay")}
            </ButtonV2>
            <ButtonV2
              variant="brand"
              size="md"
              onClick={onDiscard}
              data-testid="wizard-discard-confirm"
            >
              {t("subIBReport_wizardDiscard", "Leave")}
            </ButtonV2>
          </WizardFooter>
        </div>
      </div>
    </div>
  );
};
